<template>
  <div
    class="service-list"
    :class="{ 'is-app': $store.state.isApp }"
  >
    <div
      v-if="$store.state.isApp || filteredTypeAttributeList.length > 1"
      class="service-list__header"
    >
      <v-tabs
        v-model="selectedMainTab"
        fixed-tabs
      >
        <v-tab
          v-for="(attribute) in filteredTypeAttributeList"
          :key="attribute.Id"
          :value="attribute.Id"
          @click="onTabClick(attribute)"
        >
          <span
            :ref="`tabTextSpan${attribute.Id}`"
            class="service-tab-text"
          >{{ attribute.Description }}</span>
        </v-tab>
      </v-tabs>
    </div>

    <div
      v-if="mainTypeList.length > 0"
      class="service-list__content"
    >
      <!-- 左方服務類別列表 start-->
      <div class="service-list__menu-list">
        <v-list
          v-if="!isLoading"
        >
          <v-list-item-group
            v-model="selectedCategoryIndex"
            mandatory
          >
            <v-list-item
              v-for="(mainType, index) in mainTypeList"
              :key="index"
              :value="index"
              class="category-item"
              @click="onCategoryClick(mainType, index)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ mainType.MainTypeName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
        <template v-else>
          <v-skeleton-loader
            v-for="x in 20"
            :key="x"
            type="list-item"
          />
        </template>
      </div>
      <!-- 左方服務類別列表 end-->
      <!-- 右方服務列表 start-->
      <div class="service-list__content-list">
        <div
          v-if="selectedServiceOfCgy && !isLoading && groupedServices.length > 0"
          class="h-100"
        >
          <!-- 服務顯示區塊 -->
          <div class="service__wrapper">
            <!-- Banner 顯示 -->
            <div class="service__container">
              <div
                v-if="selectedServiceOfCgy.MainTypeBannerUrl"
                class="service__banner"
              >
                <img
                  :src="selectedServiceOfCgy.MainTypeBannerUrl"
                >
              </div>
            </div>
            <!-- 子類別選擇器 -->
            <v-chip-group
              v-model="selectedSubTypeId"
              mandatory
              @change="onSubTypeChange"
            >
              <v-chip
                v-for="(group, index) in groupedServices"
                :key="index"
                :value="group.typeInfo.TypeId"
                class="sub-type-chip"
              >
                <!-- 只有精選服務才顯示 icon -->
                <i
                  v-if="isSelection && group.typeInfo.IconName"
                  class="icon service-icon"
                  :class="{ [`icon-${group.typeInfo.IconName}`]: true }"
                >
                  <span class="path1" /><span class="path2" />
                </i>
                {{ group.typeInfo.TypeName }}
              </v-chip>
            </v-chip-group>

            <!-- 服務內容列表 -->
            <div
              class="service__content"
            >
              <div
                v-for="(group, index) in groupedServices"
                :key="index"
                class="service__sub-group"
              >
                <h4
                  v-if="group.typeInfo"
                  :id="`highlight-category-${group.typeInfo.TypeId}`"
                  class="service__sub-group-title"
                >
                  <!-- 只有精選服務才顯示 icon -->
                  <i
                    v-if="isSelection && group.typeInfo.IconName"
                    class="icon service-icon"
                    :class="{ [`icon-${group.typeInfo.IconName}`]: true }"
                  >
                    <span class="path1" /><span class="path2" />
                  </i>
                  {{ group.typeInfo.TypeName }}
                </h4>
                <ServiceCgyGroup
                  :items="group.services"
                  :type-parent="selectedServiceOfCgy.MainTypeId"
                  :main-type-name="selectedServiceOfCgy.MainTypeName"
                  :tab-name="currentTabName"
                />
              </div>
            </div>
          </div>
        </div>
        <template v-else>
          <img
            class="service-item-skeleton__banner"
            src="@/assets/image/article/default-cover.png"
          >
          <div
            v-for="x in 6"
            :key="x"
            class="service-item-skeleton__container"
          >
            <div class="service-item-skeleton__img">
              <v-skeleton-loader type="heading" />
            </div>
            <div class="service-item-skeleton__desc">
              <v-skeleton-loader type="sentences" />
            </div>
          </div>
        </template>
      </div>
      <!-- 右方服務列表 end-->
    </div>
    <div
      v-else-if="!isLoading"
      class="no-service"
    >
      <img
        src="@/assets/image/empty-status.svg"
        alt="No Services"
      >
      <p>服務已下架</p>
    </div>
  </div>
</template>

<script>
import ServiceCgyGroup from '../components/ServiceCgyGroup'

// 常數定義
const FEATURED_SERVICE_TYPE_ID = -999 // 精選分類的固定 ID

export default {
  name: 'ServiceList',
  components: {
    ServiceCgyGroup
  },
  async asyncData ({ $axios, route, store }) {
    try {
      // 在 SSR 端取得 typeAttributeList
      const { data } = await $axios.get('/api/Main/GetMainTypeAttributeTypes', { useCache: true })
      const typeAttributeList = data?.Data || []

      // 根據模式過濾（非 app 模式只顯示 HomeVisit）
      const isApp = store.state.isApp
      const filteredList = isApp
        ? typeAttributeList
        : typeAttributeList.filter(attr => attr.Name === 'HomeVisit')

      // 根據 URL 參數計算初始 selectedMainTab
      const tabParam = route.query.mainTab
      const n = parseInt(tabParam, 10)
      const validAttribute = filteredList.find(attr => attr.Id === n)

      let selectedMainTab
      if (validAttribute) {
        selectedMainTab = n
      } else if (filteredList.length > 0) {
        selectedMainTab = filteredList[0].Id
      } else {
        selectedMainTab = 0
      }

      return {
        typeAttributeList,
        selectedMainTab
      }
    } catch (error) {
      console.error('asyncData error:', error)
      return {
        typeAttributeList: [],
        selectedMainTab: 0
      }
    }
  },
  data () {
    return {
      selectedMainTab: 0,
      selectedCategoryIndex: 0, // 預設選擇"精選"
      selectedSubTypeId: 0,
      isLoading: true,
      typeAttributeList: [],
      mainTypeList: [],
      allTypeData: null, // TypeList API 資料
      updateUrlDebounceTimer: null // 防抖計時器
    }
  },
  computed: {
    /** 過濾後的分類屬性列表 - 非 app 模式只顯示 HomeVisit */
    filteredTypeAttributeList () {
      if (!this.$store.state.isApp) {
        return this.typeAttributeList.filter(attr => attr.Name === 'HomeVisit')
      }
      return this.typeAttributeList
    },
    /** 當前選中的 Tab 名稱 */
    currentTabName () {
      const tab = this.filteredTypeAttributeList.find(tab => tab.Id === this.selectedMainTab)
      return tab ? tab.Description : ''
    },
    /** 依據左方 主分類 選擇的服務 */
    selectedServiceOfCgy () {
      if (!Array.isArray(this.mainTypeList) || this.selectedCategoryIndex < 0) {
        return null
      }
      return this.mainTypeList[this.selectedCategoryIndex]
    },
    /** 確認是否為 "精選" 分類 */
    isSelection () {
      // 精選分類的固定 ID
      return this.mainTypeList[this.selectedCategoryIndex]?.MainTypeId === FEATURED_SERVICE_TYPE_ID
    },
    /** * 獲取有服務的分組資料 */
    groupedServices () {
      // 按子類別分組顯示所有服務
      if (!this.selectedServiceOfCgy?.Types) {
        return []
      }
      return this.selectedServiceOfCgy.Types
        .map(subType => ({
          typeInfo: subType,
          services: subType.Services || []
        }))
    }
  },
  watch: {
    // 監聽主頁籤變更：更新滑桿寬度並同步網址參數
    selectedMainTab (val, oldVal) {
      if (val === oldVal) return
      // 等待 DOM 更新後再更新滑桿寬度
      this.$nextTick(() => {
        this.updateSliderWidth()
      })
      // 觸發防抖更新 URL
      this.updateUrlWithDebounce()
      // 從已存資料中篩選對應 tab 的服務數據
      if (val >= 0 && this.allTypeData) {
        this.updateMainTypeListFromCache()
      }
    },
    selectedCategoryIndex (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.selectedSubTypeId = 0
        // 如果新分類有子分類，設置第一個子分類為預設選中
        this.$nextTick(() => {
          const currentService = this.selectedServiceOfCgy
          if (currentService && currentService.Types && currentService.Types.length > 0) {
            this.selectedSubTypeId = currentService.Types[0].TypeId
          }
        })
        // 觸發防抖更新 URL
        this.updateUrlWithDebounce()
      }
    }
  },
  async mounted () {
    this.$nextTick(async () => {
      this.updateSliderWidth()

      await this.fetchAllServiceData()

      this.handleQueryCategory()

      // 發送母分類瀏覽事件
      if (this.currentTabName) {
        this.$gaLogEvent('全部分類頁', `${this.currentTabName}_瀏覽`)
      }
    })
  },
  beforeDestroy () {
    // 清理防抖計時器
    if (this.updateUrlDebounceTimer) {
      clearTimeout(this.updateUrlDebounceTimer)
      this.updateUrlDebounceTimer = null
    }
  },
  methods: {
    async fetchAllServiceData () {
      if (this.allTypeData) {
        this.updateMainTypeListFromCache()
        return
      }

      this.isLoading = true

      try {
        const isApp = this.$store.state.isApp
        const regionId = this.$route.query.regionId || null
        const postData = isApp ? { HasApp: true } : { HasWeb: true }

        const userDefaultCommunity = await this.$store.dispatch('getUserDefaultCommunity', null, { root: true })
        if (userDefaultCommunity?.AreaId) {
          postData.RegionId = [userDefaultCommunity.AreaId]
          postData.DefaultCommunityId = userDefaultCommunity.Id
        } else if (regionId) {
          postData.RegionId = [parseInt(regionId)]
        } else {
          postData.RegionId = [28] // 預設台北萬華
        }

        const getTypeListAPI = async () => {
          const response = await this.$axios.get('api/Main/TypeList', { params: postData })
          return response?.data?.Data?.TypeAttributes ? response.data : { Data: { TypeAttributes: [] } }
        }

        let response = await getTypeListAPI()
        // 檢查 TypeAttributes.MainTypes 是否存在，若都不存在，則在打一次 regionId 為 28 的 API
        const hasMainTypes = response.Data.TypeAttributes.some(attr =>
          attr.MainTypes && attr.MainTypes.length > 0
        )
        if (!hasMainTypes && postData.RegionId.includes(28) === false) {
          postData.RegionId = [28] // 強制改為台北萬華
          response = await getTypeListAPI()
        }

        this.allTypeData = response.Data
        this.updateMainTypeListFromCache()
      } catch (err) {
        console.error('TypeList API error:', err)
      } finally {
        this.isLoading = false
      }
    },
    /**
     * 處理查詢參數中的 category，根據模式和現有 mainTab 參數決定目標屬性
     * 在 app 模式下，依照 mainTab 參數 -> HomeVisit -> 其他屬性 的優先順序選擇
     * 在 web 模式下，只允許 HomeVisit 屬性
     */
    handleQueryCategory () {
      const queryCategory = this.$route.query.category
      const queryMainTab = this.$route.query.mainTab

      if (!queryCategory || !this.allTypeData?.TypeAttributes) return

      const targetCategory = parseInt(queryCategory)

      // 從所有 TypeAttributes 中找到包含該 category 的屬性
      const attributesWithCategory = this.allTypeData.TypeAttributes.filter(attr =>
        attr.MainTypes?.some(mainType => mainType.MainTypeId === targetCategory)
      )

      if (attributesWithCategory.length === 0) return

      // 根據模式選擇目標屬性
      let targetAttribute
      if (!this.$store.state.isApp) {
        // web 模式：只允許 HomeVisit
        targetAttribute = attributesWithCategory.find(attr => attr.TypeAttribute === 'HomeVisit')
        if (!targetAttribute) return // web 模式下如果沒有 HomeVisit 就直接返回
      } else {
        // app 模式：如果已有 mainTab 參數，優先使用當前選中的 mainTab 對應的屬性
        if (queryMainTab) {
          const currentSelectedAttribute = this.filteredTypeAttributeList.find(attr => attr.Id === this.selectedMainTab)
          if (currentSelectedAttribute) {
            targetAttribute = attributesWithCategory.find(attr => attr.TypeAttribute === currentSelectedAttribute.Name)
          }
        }
        // 如果沒有找到對應的屬性，則優先選擇 HomeVisit，找不到就從剩下的屬性中選第一個
        if (!targetAttribute) {
          const homeVisitAttribute = attributesWithCategory.find(attr => attr.TypeAttribute === 'HomeVisit')
          if (homeVisitAttribute) {
            targetAttribute = homeVisitAttribute
          } else {
            targetAttribute = attributesWithCategory.find(attr => attr.TypeAttribute !== 'HomeVisit')
          }
        }
      }

      const targetTab = this.filteredTypeAttributeList.find(tab => tab.Name === targetAttribute.TypeAttribute)
      if (targetTab) {
        if (targetTab.Id !== this.selectedMainTab) {
          this.selectedMainTab = targetTab.Id
          this.$nextTick(() => this.handleCategoryScroll(targetCategory))
        } else {
          this.handleCategoryScroll(targetCategory)
        }
      }
    },
    /**
     * 根據目標分類 ID 滾動到左側分類列表中的對應項目
     * @param {string|number} targetCategory - 目標分類的 MainTypeId
     */
    handleCategoryScroll (targetCategory) {
      if (!this.mainTypeList.length) return
      const targetIndex = this.mainTypeList.findIndex(service => service.MainTypeId === targetCategory)
      if (targetIndex !== -1) {
        this.selectedCategoryIndex = targetIndex
        this.$nextTick(() => {
          this.scrollToCategoryItem(targetIndex)
        })
      }
    },
    /**
     * 處理 Tab 點擊事件，只在真正的使用者點擊時發送 GA 事件
     * @param {Object} attribute - 被點擊的 tab 屬性物件
     */
    onTabClick (attribute) {
      this.$gaLogEvent('全部分類頁', `${attribute.Description}_點擊`)
    },
    /**
     * 處理左側分類點擊事件，只在真正的使用者點擊時發送 GA 事件
     * @param {Object} mainType - 被點擊的分類物件
     */
    onCategoryClick (mainType) {
      // 發送 GA 事件：全部分類頁_{母分類Tab}_專館_{mainTypeName}
      this.$gaLogEvent('全部分類頁', `${this.currentTabName}_專館_${mainType.MainTypeName}`)
    },
    /**
     * 處理子分類切換事件，滾動到對應的子分類區塊
     * @param {string|number} val - 選中的子分類 TypeId
     */
    onSubTypeChange (val) {
      if (val) {
        this.$nextTick(() => {
          this.scrollToSubCategory(val)
        })
      }
    },
    /**
     * 滾動到指定的子分類區塊
     * @param {string|number} id - 子分類的 TypeId
     */
    scrollToSubCategory (id) {
      const ele = document.getElementById(`highlight-category-${id}`)
      if (!ele) return

      const contentList = document.querySelector('.service__content')
      if (!contentList) return

      // 取得元素相對於滾動容器的位置
      const contentListRect = contentList.getBoundingClientRect()
      const eleRect = ele.getBoundingClientRect()

      // 計算相對位置
      const relativeTop = eleRect.top - contentListRect.top + contentList.scrollTop

      // 執行滾動
      contentList.scrollTo({
        top: relativeTop,
        behavior: 'smooth'
      })
    },
    /**
     * 滾動左側分類列表，使選中的分類項目可見
     * @param {number} index - 要滾動到的分類索引
     */
    scrollToCategoryItem (index) {
      const categoryItems = document.querySelectorAll('.category-item')
      if (categoryItems[index]) {
        categoryItems[index].scrollIntoView({
          behavior: 'smooth'
        })
      }
    },
    /**
     * 從快取的資料中更新主分類列表，根據當前選中的 mainTab 和模式篩選對應的服務分類
     * web 模式：僅顯示 HomeVisit（到府服務）分類
     * app 模式：顯示當前選中 mainTab 對應的所有分類
     */
    updateMainTypeListFromCache () {
      if (!this.allTypeData || !this.allTypeData.TypeAttributes) return

      // 如果非 app 模式，強制只顯示 HomeVisit（到府服務）分類
      if (!this.$store.state.isApp) {
        const homeVisitAttribute = this.allTypeData.TypeAttributes.find(attr => attr.TypeAttribute === 'HomeVisit')
        if (homeVisitAttribute && homeVisitAttribute.MainTypes) {
          this.mainTypeList = homeVisitAttribute.MainTypes.filter(mainType => mainType.Types && mainType.Types.length > 0)
          this.selectedCategoryIndex = 0
        } else {
          this.mainTypeList = []
        }
        return
      }

      // app 模式下正常顯示所有分類
      const selectedAttribute = this.filteredTypeAttributeList.find(attr => attr.Id === this.selectedMainTab)
      const selectedAttributeName = selectedAttribute?.Name || ''

      const currentAttribute = this.allTypeData.TypeAttributes.find(attr => attr.TypeAttribute === selectedAttributeName)
      if (currentAttribute && currentAttribute.MainTypes) {
        this.mainTypeList = currentAttribute.MainTypes.filter(mainType => mainType.Types && mainType.Types.length > 0)
        this.selectedCategoryIndex = 0
      } else {
        this.mainTypeList = []
      }
    },
    /**
     * 防抖更新 URL 參數，避免快速連續更新
     * @param {number} delay - 防抖延遲時間（毫秒），預設 100ms
     */
    updateUrlWithDebounce (delay = 100) {
      // 清除之前的計時器
      if (this.updateUrlDebounceTimer) {
        clearTimeout(this.updateUrlDebounceTimer)
      }

      // 設定新的計時器
      this.updateUrlDebounceTimer = setTimeout(() => {
        this.updateUrlQuery()
        this.updateUrlDebounceTimer = null
      }, delay)
    },
    /**
     * 更新 URL 查詢參數
     */
    updateUrlQuery () {
      const query = {
        mainTab: String(this.selectedMainTab)
      }

      // 添加 category 參數
      if (this.selectedServiceOfCgy && this.selectedServiceOfCgy.MainTypeId) {
        query.category = String(this.selectedServiceOfCgy.MainTypeId)
      } else if (this.$route.query.category) {
        query.category = this.$route.query.category
      }

      // 保留 regionId 參數
      if (this.$route.query.regionId) {
        query.regionId = this.$route.query.regionId
      }

      // 檢查是否與當前 URL 參數相同，避免重複導航
      const currentQueryString = JSON.stringify(this.$route.query)
      const newQueryString = JSON.stringify(query)
      if (currentQueryString !== newQueryString) {
        this.$router.replace({ query }).catch(err => {
          console.error('Router replace error:', err)
        })
      }
    },
    updateSliderWidth () {
      this.$nextTick(() => {
        const activeId = this.selectedMainTab
        const textSpanRef = this.$refs[`tabTextSpan${activeId}`]

        if (textSpanRef && textSpanRef[0]) {
          const textWidth = textSpanRef[0].offsetWidth
          document.documentElement.style.setProperty('--tab-slider-width', `${textWidth}px`)
          document.documentElement.style.setProperty('--tab-slider-opacity', '1')
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.service-list {
  display: flex;
  flex-direction: column;
  height: fit-content;
  max-height: calc(var(--vh, 1vh) * 100 - #{$headerHeight});
  width: 100%;
  position: relative;

  &.is-app {
    max-height: calc(var(--vh, 1vh) * 100 - #{$fixedFooterHeight});
    min-height: calc(var(--vh, 1vh) * 100 - #{$fixedFooterHeight});
    padding-bottom: 0;
  }

  &__header {
    position: sticky;
    top: 0;
    z-index: 1;
    border-bottom: 1px solid $color-border-primary;
    flex-shrink: 0;
  }

  .v-tabs {
    &::v-deep {
      .v-tabs-bar {
        background-color: $color-background-secondary;
      }

      .v-tab {
        color: $color-disabled;
        font-weight: 500;
        font-size: pxTorem(16px);
        box-sizing: border-box;
        padding: 0 8px;

        &--active {
          color: $color-primary;
          font-weight: bold;
        }
        .service-tab-text {
          padding: 12px 0;
        }
      }

      .v-tabs-slider-wrapper {
        height: 4px !important;
      }

      .v-tabs-slider {
        background-color: transparent !important;
        height: 4px;
        display: flex;
        justify-content: center;
        align-items: center;

        &::after {
          content: '';
          width: var(--tab-slider-width, 0px);
          height: 4px;
          background-color: $color-primary;
          transition: width 0.3s ease;
          opacity: var(--tab-slider-opacity, 0);
          border-radius: 2px;
        }
      }
    }
  }

  &__content {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  &__menu-list {
    min-width: 100px;
    max-height: 100%;
    flex: 1;
    overflow: auto;

    .v-list {
      padding: 0;
    }
  }

  &__content-list {
    flex: 3;
    overflow: auto;
    border-left: 1px solid $color-border-primary;

    ::v-deep .v-slide-group__content {
      padding: 4px 8px;
    }
    .v-chip {
      &.sub-type-chip {
        background: $color-background-primary;
        height: 34px;
        font-weight: 600;
        font-size: pxTorem(13px);

        &::after {
          background-color: transparent;
        }
      }

      &--active {
        &.sub-type-chip {
          background: $color-primary;
          color: $color-text-inverse;
        }
      }
    }
  }
}

.category-item {
  min-height: 25px;
  background-color: $color-background-primary;
  transition: background-color 0.2s;

  &.v-list-item--active {
    background-color: $color-background-secondary;
    color: $color-primary;
    font-weight: 700;

    &.v-list-item--link::before {
      background: #F0F0F0;
    }
  }

  &.v-list-item {
    padding: 0;
  }

  .v-ripple__container {
    background-color: $color-background-secondary;
  }
}

// 統一的服務容器樣式
.service {
  &__wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  &__banner {
    width: 100%;

    img {
      display: block;
      width: 100%;
    }
  }

  &__content {
    display: inline-flex;
    flex-direction: column;
    gap: 8px;
    background-color: #F0F0F0;
    overflow: auto;
  }

  &__sub-group {
    background: $color-background-secondary;
  }

  &__sub-group-title {
    padding: 8px 12px 0;
    font-size: pxTorem(17px);
    line-height: pxTorem(24px);
  }
}

// 服務圖示樣式
.service-icon {
  margin-right: 4px;
}

// 左方列表樣式調整
.v-list-item {
  &__content {
    padding: 15.5px;
  }

  &__title {
    text-align: center;
    text-overflow: initial;
    white-space: break-spaces;
    font-size: pxTorem(15px);
    line-height: pxTorem(21px);
  }
}

.h-100 {
  height: 100%;
}

// skeleton loader

.service-list__menu-list {
  ::v-deep .v-skeleton-loader {
    border-radius: 0;

    &__list-item {
      padding: 0;
      border-radius: 0;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: flex-end;

      &::before {
        content: '';
        position: absolute;
        width: 80%;
        height: 1px;
        background-color: $color-border-primary;
        z-index: 1;
      }
    }

    &__text {
      height: 100%;
      border-radius: 0;
      background-color: $color-background-primary;
    }
  }
}

.service-item-skeleton {
  &__banner {
    max-width: 100%;
  }

  &__container {
    display: flex;
  }

  &__img {
    width: 68px;
    height: 68px;
    margin: 0 8px 20px 8px;

    .v-skeleton-loader {
      display: grid;
      place-items: center;
      height: 100%;
    }

    ::v-deep .v-skeleton-loader__bone {
      width: 68px;
      height: 68px;
    }
  }

  &__desc {
    flex: 1;
    padding: 8px 8px 0;
  }
}

// 無服務提示樣式
.no-service {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  padding-top: 128px;

  img {
    max-width: 100px;
    margin-bottom: 16px;
  }

  p {
    font-size: pxTorem(16px);
    margin: 0;
    color: $color-caption;
  }
}
</style>
